import React, { useState } from 'react';
import { AlertCircle, ArrowLeft, Droplets, Zap, CheckCircle, Loader2 } from 'lucide-react';
import type { AgriIrrigation, LocationData } from '../types';
import { supabase } from '../lib/supabase';
import type { UserProfile } from '../types';

interface AgriDesignRequirementsProps {
  onNext: (data: Partial<LocationData>) => void;
  onBack: () => void;
  initialData?: Partial<LocationData>;
  currentProjectId?: string | null;
  user?: UserProfile | null;
}

interface AgriFormData {
  
  // Section 2: Water Demand & Schedule
  daily_water_need: number;
  use_helper: boolean;
  helper_area: number;
  helper_crop: string;
  helper_method: string;
  helper_hours: number;
  
  
  // Section 3: Power
  use_grid: boolean;
  use_diesel: boolean;
  
  // Grid settings
  grid_same_as_pump: boolean;
  grid_phase: '1' | '3';
  grid_voltage: number;
  grid_export_allowed: boolean;
  
  // Diesel settings  
  diesel_same_as_grid_or_pump: boolean;
  diesel_size_kw: number;
  diesel_phase: '1' | '3';
  diesel_voltage: number;
  
  // Water source details
  water_source_type: 'well' | 'open_source';
  wells_count: number;
  wells: Array<{
    id: number;
    measured_flow: number | null;
    salinity: number | null;
    tdh: number | null;
    well_depth: number | null;
    height_to_tank: number | null;
    // New TDH calculation fields
    end_use_type: 'open_tank' | 'drip_lines' | 'sprinklers' | 'high_pressure_sprinkler' | 'pivot_linear';
    filter_allowance: 'none' | 'clean' | 'dirty';
    pipe_configuration: 'short_0_50m_0_2b' | 'short_50_150m_≤4b' | 'typical_150_300m_5_8b' | 'long_300_600m_9_12b' | 'verylong_600_1000m_13_20b' | 'extreme_>1000m_manyb';
    pipe_size: 'small' | 'medium' | 'large';
    computed_pressure_bar: number;
    computed_pipe_loss_percent: number;
    // Legacy fields for backward compatibility
    pressure_type?: 'open_tank' | 'drip' | 'sprinklers' | 'custom';
    pressure_bar?: number;
    pipe_loss_type?: 'short' | 'typical' | 'long';
    pipe_loss_percent?: number;
  }>;
  
  // Pump details
  has_pumps: boolean;
  pumps_count: number;
  pumps: Array<{
    id: number;
    pump_type: 'submersible' | 'surface';
    serves_well: number | 'others' | null;
    pump_category: 'electrical' | 'mechanical';
    // Electrical pump fields
    frequency?: '50' | '60';
    phase?: '1' | '3';
    voltage?: number;
    // Mechanical pump fields
    horsepower?: number;
    consumption?: number;
    consumption_timeframe?: 'daily' | 'weekly' | 'monthly';
  }>;
  
  // Uploads (all optional)
  pump_datasheet: File | null;
  generator_datasheet: File | null;
  electricity_bill: File | null;
  site_photos: File | null;
  
  // Consent
  contact_consent: boolean;
  data_use_consent: boolean;
  
  // HP Estimation
  hp_estimated: boolean;
  hp_estimation_method: 'generator' | 'amps' | 'flow' | null;
  hp_estimation_confidence: 'high' | 'medium' | 'low' | null;
}

export function AgriDesignRequirements({ onNext, onBack, initialData, currentProjectId, user }: AgriDesignRequirementsProps) {
  const [form, setForm] = useState<AgriFormData>(() => {
    const existing = initialData?.agri_irrigation;
    return {
      
      // Section 2: Water Demand & Schedule
      daily_water_need: 0,
      use_helper: false,
      helper_area: 0,
      helper_crop: '',
      helper_method: '',
      helper_hours: 6,
      
      
      // Section 3: Power
      use_grid: true,
      use_diesel: false,
      
      // Grid settings
      grid_same_as_pump: true,
      grid_phase: '3',
      grid_voltage: 400,
      grid_export_allowed: false,
      
      // Diesel settings  
      diesel_same_as_grid_or_pump: true,
      diesel_size_kw: 0,
      diesel_phase: '3',
      diesel_voltage: 400,
      
    // Water source details
    water_source_type: existing?.water_source_type || 'well',
    wells_count: existing?.wells_count ?? 0,
    wells: (existing as any)?.wells?.map((well: any) => {
      // Backward compatibility mapping
      const mapLegacyValues = (well: any) => {
        // Map legacy pressure values to new format
        let endUseType = well.end_use_type || 'drip_lines';
        let filterAllowance = well.filter_allowance || 'none';
        
        if (well.pressure_type && !well.end_use_type) {
          switch (well.pressure_type) {
            case 'open_tank':
              endUseType = 'open_tank';
              filterAllowance = 'none';
              break;
            case 'drip':
              endUseType = 'drip_lines';
              filterAllowance = 'none';
              break;
            case 'sprinklers':
              endUseType = 'sprinklers';
              filterAllowance = 'none';
              break;
            default:
              endUseType = 'drip_lines';
              filterAllowance = 'none';
          }
        }
        
        // Map legacy pipe loss values to new format
        let pipeConfiguration = well.pipe_configuration || 'typical_150_300m_5_8b';
        let pipeSize = well.pipe_size || 'medium';
        
        if (well.pipe_loss_type && !well.pipe_configuration) {
          switch (well.pipe_loss_type) {
            case 'short':
              pipeConfiguration = 'short_50_150m_≤4b';
              break;
            case 'typical':
              pipeConfiguration = 'typical_150_300m_5_8b';
              break;
            case 'long':
              pipeConfiguration = 'long_300_600m_9_12b';
              break;
            default:
              pipeConfiguration = 'typical_150_300m_5_8b';
          }
        }
        
        return {
          endUseType,
          filterAllowance,
          pipeConfiguration,
          pipeSize
        };
      };
      
      const mapped = mapLegacyValues(well);
      const computedPressure = calculateEndUsePressure(mapped.endUseType, mapped.filterAllowance);
      const computedPipeLoss = calculatePipeLoss(mapped.pipeConfiguration, mapped.pipeSize);
      
      return {
        id: well.id || 0,
        measured_flow: well.measured_flow || null,
        salinity: well.salinity || null,
        tdh: well.tdh || null,
        well_depth: well.well_depth || null,
        height_to_tank: well.height_to_tank || null,
        // New fields
        end_use_type: mapped.endUseType,
        filter_allowance: mapped.filterAllowance,
        pipe_configuration: mapped.pipeConfiguration,
        pipe_size: mapped.pipeSize,
        computed_pressure_bar: computedPressure,
        computed_pipe_loss_percent: computedPipeLoss * 100, // Convert to percentage
        // Legacy fields for backward compatibility
        pressure_type: well.pressure_type || 'drip',
        pressure_bar: well.pressure_bar || 2.0,
        pipe_loss_type: well.pipe_loss_type || 'typical',
        pipe_loss_percent: well.pipe_loss_percent || 15,
      };
    }) ?? [],
    
    // Pump details
    has_pumps: existing?.has_pumps ?? false,
    pumps_count: existing?.pumps_count ?? 0,
    pumps: (existing as any)?.pumps?.map((pump: any) => ({
      id: pump.id || 0,
      pump_type: pump.pump_type || 'submersible',
      serves_well: pump.serves_well || null,
      pump_category: pump.pump_category || 'electrical',
      // Electrical pump fields
      frequency: pump.frequency || '50',
      phase: pump.phase || '3',
      voltage: pump.voltage || 400,
      // Mechanical pump fields
      horsepower: pump.horsepower || 0,
      consumption: pump.consumption || 0,
      consumption_timeframe: pump.consumption_timeframe || 'daily',
    })) ?? [],
      
      // Uploads
      pump_datasheet: null,
      generator_datasheet: null,
      electricity_bill: null,
      site_photos: null,
      
      // Consent
      contact_consent: false,
      data_use_consent: false,
      
      // HP Estimation
      hp_estimated: false,
      hp_estimation_method: null,
      hp_estimation_confidence: null,
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [waterEstimateApplied, setWaterEstimateApplied] = useState(false);
  const [showTDHHelper, setShowTDHHelper] = useState<{ [key: number]: boolean }>({});
  
  // Database operation states
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  // TDH calculation helper functions
  const calculateEndUsePressure = (endUseType: string, filterAllowance: string) => {
    const basePressures: Record<string, number> = {
      'open_tank': 0.0,
      'drip_lines': 2.0,
      'sprinklers': 2.5,
      'high_pressure_sprinkler': 4.5,
      'pivot_linear': 1.0
    };
    
    const filterAllowances: Record<string, number> = {
      'none': 0.0,
      'clean': 0.2,
      'dirty': 0.4
    };
    
    return basePressures[endUseType] + filterAllowances[filterAllowance];
  };

  const calculatePipeLoss = (pipeConfig: string, pipeSize: string) => {
    const basePercentages: Record<string, number> = {
      'short_0_50m_0_2b': 0.08,
      'short_50_150m_≤4b': 0.10,
      'typical_150_300m_5_8b': 0.15,
      'long_300_600m_9_12b': 0.20,
      'verylong_600_1000m_13_20b': 0.25,
      'extreme_>1000m_manyb': 0.35
    };
    
    const sizeMultipliers: Record<string, number> = {
      'small': 1.2,
      'medium': 1.0,
      'large': 0.85
    };
    
    const basePercent = basePercentages[pipeConfig] || 0.15;
    const multiplier = sizeMultipliers[pipeSize] || 1.0;
    
    return basePercent * multiplier;
  };

  const calculateTDH = (well: any) => {
    if (!well.well_depth || !well.height_to_tank) return null;

    const wellDepth = well.well_depth;
    const heightToTank = well.height_to_tank;
    
    // Use new calculation if available, otherwise fall back to legacy
    let pressureBar: number;
    let pipeLossPercent: number;
    
    if (well.computed_pressure_bar !== undefined && well.computed_pipe_loss_percent !== undefined) {
      pressureBar = well.computed_pressure_bar;
      pipeLossPercent = well.computed_pipe_loss_percent;
    } else {
      // Legacy fallback
      pressureBar = well.pressure_bar || 2.0;
      pipeLossPercent = well.pipe_loss_percent || 15;
    }
    
    // Convert pressure from bar to meters (1 bar = 10.2 meters)
    const pressureMeters = pressureBar * 10.2;
    
    // Calculate base head
    const baseHead = wellDepth + heightToTank + pressureMeters;
    
    // Add pipe loss percentage
    const totalHead = baseHead * (1 + pipeLossPercent / 100);
    
    return Math.round(totalHead * 10) / 10; // Round to 1 decimal place
  };

  // File upload function
  const uploadFile = async (file: File, fileType: string): Promise<string | null> => {
    if (!currentProjectId) {
      throw new Error('No project ID available');
    }

    try {
      const fileName = `agricultural/${fileType}/${currentProjectId}/${Date.now()}-${file.name}`;
      
      const { error: uploadError } = await supabase.storage
        .from('project_files')
        .upload(fileName, file);

      if (uploadError) {
        console.error(`Error uploading ${fileType}:`, uploadError);
        throw new Error(`Failed to upload ${fileType}: ${uploadError.message}`);
      }

      const { data: urlData } = supabase.storage
        .from('project_files')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      throw error;
    }
  };

  // Main database save function
  const saveAgriculturalData = async (formData: AgriFormData): Promise<void> => {
    if (!currentProjectId || !user) {
      throw new Error('Project ID or user not available');
    }

    try {
      // 1. Upload files to Supabase storage
      const uploadPromises: Promise<{ type: string; url: string | null }>[] = [];
      
      if (formData.pump_datasheet) {
        uploadPromises.push(
          uploadFile(formData.pump_datasheet, 'pump_datasheet')
            .then(url => ({ type: 'pump_datasheet', url }))
        );
      }
      
      if (formData.generator_datasheet) {
        uploadPromises.push(
          uploadFile(formData.generator_datasheet, 'generator_datasheet')
            .then(url => ({ type: 'generator_datasheet', url }))
        );
      }
      
      if (formData.electricity_bill) {
        uploadPromises.push(
          uploadFile(formData.electricity_bill, 'electricity_bill')
            .then(url => ({ type: 'electricity_bill', url }))
        );
      }
      
      if (formData.site_photos) {
        uploadPromises.push(
          uploadFile(formData.site_photos, 'site_photos')
            .then(url => ({ type: 'site_photos', url }))
        );
      }

      // Wait for all uploads to complete
      const uploadResults = await Promise.all(uploadPromises);
      const uploadUrls: Record<string, string> = {};
      uploadResults.forEach(result => {
        if (result.url) {
          uploadUrls[result.type] = result.url;
        }
      });

      // 2. Prepare agricultural data structure
      const agriculturalData = {
        water_source: {
          type: formData.water_source_type,
          wells_count: formData.wells_count,
          wells: formData.wells.map(well => ({
            id: well.id,
            measured_flow: well.measured_flow,
            salinity: well.salinity,
            tdh: well.tdh,
            well_depth: well.well_depth,
            height_to_tank: well.height_to_tank,
            end_use_type: well.end_use_type,
            filter_allowance: well.filter_allowance,
            pipe_configuration: well.pipe_configuration,
            pipe_size: well.pipe_size,
            computed_pressure_bar: well.computed_pressure_bar,
            computed_pipe_loss_percent: well.computed_pipe_loss_percent,
            // Legacy fields for backward compatibility
            pressure_type: well.pressure_type,
            pressure_bar: well.pressure_bar,
            pipe_loss_type: well.pipe_loss_type,
            pipe_loss_percent: well.pipe_loss_percent
          }))
        },
        pump_configuration: {
          has_pumps: formData.has_pumps,
          pumps_count: formData.pumps_count,
          pumps: formData.pumps.map(pump => ({
            id: pump.id,
            pump_type: pump.pump_type,
            serves_well: pump.serves_well,
            pump_category: pump.pump_category,
            frequency: pump.frequency,
            phase: pump.phase,
            voltage: pump.voltage,
            horsepower: pump.horsepower,
            consumption: pump.consumption,
            consumption_timeframe: pump.consumption_timeframe
          }))
        },
        water_demand: {
          daily_water_need: formData.daily_water_need,
          use_helper: formData.use_helper,
          helper_area: formData.helper_area,
          helper_crop: formData.helper_crop,
          helper_method: formData.helper_method,
          helper_hours: formData.helper_hours
        },
        power_sources: {
          use_grid: formData.use_grid,
          use_diesel: formData.use_diesel,
          grid_config: {
            same_as_pump: formData.grid_same_as_pump,
            phase: formData.grid_phase,
            voltage: formData.grid_voltage,
            export_allowed: formData.grid_export_allowed
          },
          diesel_config: {
            same_as_grid_or_pump: formData.diesel_same_as_grid_or_pump,
            size_kw: formData.diesel_size_kw,
            phase: formData.diesel_phase,
            voltage: formData.diesel_voltage
          }
        },
        uploads: {
          pump_datasheet_url: uploadUrls.pump_datasheet || null,
          generator_datasheet_url: uploadUrls.generator_datasheet || null,
          electricity_bill_url: uploadUrls.electricity_bill || null,
          site_photos_url: uploadUrls.site_photos || null
        },
        consent: {
          contact_consent: formData.contact_consent,
          data_use_consent: formData.data_use_consent
        },
        hp_estimation: {
          hp_estimated: formData.hp_estimated,
          hp_estimation_method: formData.hp_estimation_method,
          hp_estimation_confidence: formData.hp_estimation_confidence
        }
      };

      // 3. Get existing project data
      const { data: existingProject, error: fetchError } = await supabase
        .from('projects')
        .select('solar_design')
        .eq('id', currentProjectId)
        .single();

      if (fetchError) {
        console.error('Error fetching existing project:', fetchError);
        throw new Error('Failed to fetch existing project data');
      }

      // 4. Update solar_design with agricultural data
      const updatedSolarDesign = {
        ...existingProject.solar_design,
        agricultural_data: agriculturalData
      };

      // 5. Save to database
      const { error: updateError } = await supabase
        .from('projects')
        .update({
          solar_design: updatedSolarDesign,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentProjectId);

      if (updateError) {
        console.error('Error updating project:', updateError);
        throw new Error('Failed to save agricultural data to database');
      }

    } catch (error) {
      console.error('Error saving agricultural data:', error);
      throw error;
    }
  };

  // Power section helper functions
  const getLargestPump = () => {
    if (form.pumps.length === 0) return { phase: '3' as '1' | '3', voltage: 400, kw: 0 };
    
    // Find pump with highest estimated kW (only for electrical pumps)
    const electricalPumps = form.pumps.filter(pump => pump.pump_category === 'electrical');
    if (electricalPumps.length === 0) return { phase: '3' as '1' | '3', voltage: 400, kw: 0 };
    
    return electricalPumps.reduce((largest, pump) => {
      const pumpKw = calculatePumpKw(pump);
      return pumpKw > largest.kw ? { 
        phase: pump.phase || '3', 
        voltage: pump.voltage || 400, 
        kw: pumpKw 
      } : largest;
    }, { phase: '3' as '1' | '3', voltage: 400, kw: 0 });
  };

  const calculatePumpKw = (pump: any) => {
    // Only calculate for electrical pumps
    if (pump.pump_category !== 'electrical' || !pump.phase || !pump.voltage) {
      return 0;
    }
    
    // Rough estimation based on voltage and phase
    if (pump.phase === '1') {
      return pump.voltage * 0.1; // Rough estimate for 1-phase
    } else {
      return pump.voltage * 0.15; // Rough estimate for 3-phase
    }
  };

  // Helper function to get voltage options based on frequency and phase
  const getVoltageOptions = (frequency: string, phase: string) => {
    if (frequency === '50') {
      if (phase === '1') {
        return [{ value: 230, label: '230 V (L-N)' }];
      } else {
        return [
          { value: 400, label: '400 V (L-L) / 230 V (L-N)' },
          { value: 415, label: '415 V (L-L) / 240 V (L-N)' }
        ];
      }
    } else { // 60 Hz
      if (phase === '1') {
        return [
          { value: 120, label: '120 V' },
          { value: 240, label: '240 V (split phase)' }
        ];
      } else {
        return [
          { value: 208, label: '208 V (L-L) / 120 V (L-N)' },
          { value: 480, label: '480 V (L-L) / 277 V (L-N)' }
        ];
      }
    }
  };

  


  const calculateWaterDemand = () => {
    if (form.use_helper && form.helper_area > 0 && form.helper_crop && form.helper_method) {
      // Simple calculation based on area, crop, and method
      const cropMultiplier = {
        'wheat': 0.8,
        'corn': 1.2,
        'vegetables': 1.5,
        'fruits': 1.0,
        'cotton': 1.1
      }[form.helper_crop] || 1.0;
      
      const methodMultiplier = {
        'drip': 0.6,
        'sprinkler': 1.0,
        'flood': 1.5,
        'pivot': 0.8
      }[form.helper_method] || 1.0;
      
      return Math.round(form.helper_area * cropMultiplier * methodMultiplier * form.helper_hours);
    }
    return form.daily_water_need;
  };

  const calculateWaterEstimate = () => {
    if (form.helper_area > 0 && form.helper_hours > 0 && form.helper_crop && form.helper_method) {
      // Base rates (m³/ha/hr)
      const baseRates: Record<string, number> = {
        'drip': 6,
        'sprinkler': 10,
        'flood': 14,
        'mixed': 9
      };
      
      // Crop factors
      const cropFactors: Record<string, number> = {
        'date palms': 1.10,
        'alfalfa': 1.20,
        'wheat': 0.90,
        'vegetables': 1.00,
        'orchards': 1.00,
        'other': 1.00
      };
      
      const baseRate = baseRates[form.helper_method] || 6;
      const cropFactor = cropFactors[form.helper_crop] || 1.0;
      const seasonFactor = 1.00; // Default season factor
      
      const dailyM3 = form.helper_area * form.helper_hours * baseRate * cropFactor * seasonFactor;
      const targetFlow = dailyM3 / form.helper_hours;
      
      return {
        dailyM3: Math.round(dailyM3),
        targetFlow: Math.round(targetFlow * 10) / 10
      };
    }
    return null;
  };



  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};


    // Section 2: Water Demand - Must have either daily need or helper
    if (form.daily_water_need <= 0 && (!form.use_helper || !form.helper_area || !form.helper_crop || !form.helper_method)) {
      nextErrors.daily_water_need = 'Either daily water need or helper fields are required';
    }


    // Section 3: Power - At least one power source required
    if (!form.use_grid && !form.use_diesel) {
      nextErrors.power_sources = 'At least one power source is required';
    }

    // Diesel requires size if not same as grid/pump
    if (form.use_diesel && !form.diesel_same_as_grid_or_pump && form.diesel_size_kw <= 0) {
      nextErrors.diesel_size_kw = 'Generator size is required for diesel systems';
    }

    // Consent - Required
    if (!form.contact_consent) {
      nextErrors.contact_consent = 'Contact consent is required';
    }
    if (!form.data_use_consent) {
      nextErrors.data_use_consent = 'Data use consent is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsLoading(true);
    setSaveError(null);
    
    try {
      // Save agricultural data to database
      await saveAgriculturalData(form);
      
      // Convert to the expected format with ALL agricultural data for backward compatibility
      const agriIrrigation: AgriIrrigation = {
        // Water Demand & Schedule
        daily_water_need: form.daily_water_need,
        use_helper: form.use_helper,
        helper_area: form.helper_area,
        helper_crop: form.helper_crop,
        helper_method: form.helper_method,
        helper_hours: form.helper_hours,
        
        // Power Configuration
        use_grid: form.use_grid,
        use_diesel: form.use_diesel,
        grid_same_as_pump: form.grid_same_as_pump,
        grid_phase: form.grid_phase,
        grid_voltage: form.grid_voltage,
        grid_export_allowed: form.grid_export_allowed,
        diesel_same_as_grid_or_pump: form.diesel_same_as_grid_or_pump,
        diesel_size_kw: form.diesel_size_kw,
        diesel_phase: form.diesel_phase,
        diesel_voltage: form.diesel_voltage,
        
        // Water source details
        water_source_type: form.water_source_type,
        wells_count: form.wells_count,
        wells: form.wells,
        
        // Pump details
        has_pumps: form.has_pumps,
        pumps_count: form.pumps_count,
        pumps: form.pumps as any,
        
        // Uploads
        pump_datasheet: form.pump_datasheet,
        generator_datasheet: form.generator_datasheet,
        electricity_bill: form.electricity_bill,
        site_photos: form.site_photos,
        
        // Consent
        contact_consent: form.contact_consent,
        data_use_consent: form.data_use_consent,
        
        // HP Estimation
        hp_estimated: form.hp_estimated,
        hp_estimation_method: form.hp_estimation_method,
        hp_estimation_confidence: form.hp_estimation_confidence,
        
        // Legacy fields for backward compatibility
        pump_power_type: form.use_diesel ? 'diesel' : 'electric',
        diesel_details: form.use_diesel ? {
          well_depth_m: 20, // Default well depth
          static_water_level_m: 15, // Default static water level
          flow_rate_m3h: calculateWaterDemand() / 24,
          well_diameter_mm: 200, // Default well diameter
          pipe_diameter_mm: 100, // Default pipe diameter
          salinity_ppm: 0
        } : undefined,
        documents: {
          pump_datasheet: form.pump_datasheet,
          generator_datasheet: form.generator_datasheet,
          electricity_bill: form.electricity_bill
        }
      };
      
      onNext({ agri_irrigation: agriIrrigation });
    } catch (error) {
      console.error('Error saving agricultural data:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save agricultural data');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-indigo-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={onBack}
                  className="mr-4 p-2 hover:bg-white/20 rounded-xl transition-all duration-200 group"
                  aria-label="Go back"
                >
                  <ArrowLeft className="h-6 w-6 text-white group-hover:scale-110 transition-transform" />
                </button>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Droplets className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Agricultural Design Requirements</h2>
                    <p className="text-green-100 text-sm">Configure your irrigation system specifications</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">

              {/* Section 1: Well Details */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-green-100 rounded-lg mr-3">
                    <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Well Details</h3>
                </div>
                
                <div className="space-y-6">
                  {/* Well Selection Fields */}
                  <div className="flex flex-col lg:flex-row lg:items-start space-y-4 lg:space-y-0 lg:space-x-12">
                    <div className="space-y-2 flex-1">
                      <label className="block text-sm font-medium text-gray-700">What is your water source?</label>
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <div className="relative">
                            <input
                              type="radio"
                              name="water_source_type"
                              className="sr-only"
                              checked={form.water_source_type === 'well'}
                              onChange={() => setForm({ ...form, water_source_type: 'well' })}
                            />
                            <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                              form.water_source_type === 'well' 
                                ? 'border-green-500 bg-green-500' 
                                : 'border-gray-300 bg-white group-hover:border-green-400'
                            }`}>
                              {form.water_source_type === 'well' && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors">Well</span>
                        </label>
                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <div className="relative">
                            <input
                              type="radio"
                              name="water_source_type"
                              className="sr-only"
                              checked={form.water_source_type === 'open_source'}
                              onChange={() => setForm({ ...form, water_source_type: 'open_source' })}
                            />
                            <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                              form.water_source_type === 'open_source' 
                                ? 'border-green-500 bg-green-500' 
                                : 'border-gray-300 bg-white group-hover:border-green-400'
                            }`}>
                              {form.water_source_type === 'open_source' && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors">Open Source</span>
                        </label>
                      </div>
                    </div>
                    
                    {form.water_source_type === 'well' && (
                      <div className="space-y-2 flex-1 max-w-xs">
                        <label className="block text-sm font-medium text-gray-700">Number of Wells</label>
                        <input
                          type="number"
                          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                          value={form.wells_count}
                          onChange={(e) => {
                            const count = Math.max(0, Math.floor(Number(e.target.value) || 0));
                            const newWells = Array.from({ length: count }, (_, i) => {
                              const existingWell = form.wells[i];
                              const endUseType = existingWell?.end_use_type || 'drip_lines';
                              const filterAllowance = existingWell?.filter_allowance || 'clean';
                              const pipeConfiguration = existingWell?.pipe_configuration || 'typical_150_300m_5_8b';
                              const pipeSize = existingWell?.pipe_size || 'medium';
                              
                              const computedPressure = calculateEndUsePressure(endUseType, filterAllowance);
                              const computedPipeLoss = calculatePipeLoss(pipeConfiguration, pipeSize);
                              
                              return {
                                id: i + 1,
                                measured_flow: existingWell?.measured_flow || null,
                                salinity: existingWell?.salinity || null,
                                tdh: existingWell?.tdh || null,
                                well_depth: existingWell?.well_depth || null,
                                height_to_tank: existingWell?.height_to_tank || null,
                                // New fields
                                end_use_type: endUseType,
                                filter_allowance: filterAllowance,
                                pipe_configuration: pipeConfiguration,
                                pipe_size: pipeSize,
                                computed_pressure_bar: computedPressure,
                                computed_pipe_loss_percent: computedPipeLoss * 100,
                                // Legacy fields
                                pressure_type: existingWell?.pressure_type || 'drip',
                                pressure_bar: existingWell?.pressure_bar || 2.0,
                                pipe_loss_type: existingWell?.pipe_loss_type || 'typical',
                                pipe_loss_percent: existingWell?.pipe_loss_percent || 15,
                              };
                            });
                            setForm({ ...form, wells_count: count, wells: newWells });
                          }}
                          min={0}
                          placeholder="0"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Water Source Photos - Show based on selection */}
                  {form.water_source_type === 'well' && (
                    <div className="mt-6 flex justify-center">
                      <img 
                        src="https://www.ahwelldrilling.ca/wp-content/uploads/2017/05/waterwell.jpg" 
                        alt="Water Well" 
                        className="max-w-full h-48 object-cover rounded-lg shadow-sm"
                      />
                    </div>
                  )}
                  
                  {form.water_source_type === 'open_source' && (
                    <div className="mt-6 flex justify-center">
                      <img 
                        src="https://vid.alarabiya.net/images/2018/12/08/7e59e8e6-f079-4964-bf50-61be236e2f67/7e59e8e6-f079-4964-bf50-61be236e2f67_16x9_1200x676.jpg?width=720" 
                        alt="Open Water Source" 
                        className="max-w-full h-48 object-cover rounded-lg shadow-sm"
                      />
                    </div>
                  )}
                  
                  {/* Well Details Table - Full Width */}
                  {form.water_source_type === 'well' && form.wells_count > 0 && (
                    <>
                      {/* Separator */}
                      <div className="border-t border-gray-200"></div>
                      
                      {/* Well Details Table */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <h5 className="text-sm font-semibold text-gray-800">Well Specifications</h5>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                            {form.wells_count} well{form.wells_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        <div className="space-y-4">
                          {form.wells.map((well, index) => (
                            <div key={well.id} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-center space-x-4 mb-4">
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-sm font-semibold text-green-600">{well.id}</span>
                                  </div>
                                  <h6 className="text-sm font-semibold text-gray-800">Well #{well.id}</h6>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                
                                <div className="space-y-1">
                                  <label className="block text-xs font-medium text-gray-600">Measured Flow (m³/h)</label>
                                  <input
                                    type="number"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                    value={well.measured_flow || ''}
                                    onChange={(e) => {
                                      const newWells = [...form.wells];
                                      newWells[index].measured_flow = e.target.value ? Number(e.target.value) : null;
                                      setForm({ ...form, wells: newWells });
                                    }}
                                    min={0}
                                    placeholder="Optional"
                                  />
                                </div>
                                
                                <div className="space-y-1">
                                  <label className="block text-xs font-medium text-gray-600">Salinity (ppm)</label>
                                  <input
                                    type="number"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                    value={well.salinity || ''}
                                    onChange={(e) => {
                                      const newWells = [...form.wells];
                                      newWells[index].salinity = e.target.value ? Number(e.target.value) : null;
                                      setForm({ ...form, wells: newWells });
                                    }}
                                    min={0}
                                    placeholder="Optional"
                                  />
                                </div>
                                
                                <div className="space-y-1">
                                  <label className="block text-xs font-medium text-gray-600 flex items-center justify-between">
                                    <div className="flex items-center">
                                      TDH (m)
                                      <div className="relative group ml-1">
                                        <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center cursor-help">
                                          <span className="text-white text-xs font-bold">i</span>
                                        </div>
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                          Distance between water level and pump
                                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setShowTDHHelper(prev => ({ ...prev, [well.id]: !prev[well.id] }))}
                                      className="text-xs text-green-600 hover:text-green-800 font-medium underline"
                                    >
                                      {showTDHHelper[well.id] ? 'Hide calculation' : 'Calculate TDH'}
                                    </button>
                                  </label>
                                  <input
                                    type="number"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                    value={well.tdh || ''}
                                    onChange={(e) => {
                                      const newWells = [...form.wells];
                                      newWells[index].tdh = e.target.value ? Number(e.target.value) : null;
                                      setForm({ ...form, wells: newWells });
                                    }}
                                    min={0}
                                    step={0.1}
                                    placeholder="Auto-calculated"
                                    readOnly
                                  />
                                </div>
                              </div>
                              
                              {/* TDH Calculation Fields */}
                              {showTDHHelper[well.id] && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <h6 className="text-xs font-semibold text-gray-700 mb-3">TDH Calculation</h6>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <label className="block text-xs font-medium text-gray-600">Well Depth (m)</label>
                                    <input
                                      type="number"
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                      value={well.well_depth || ''}
                                      onChange={(e) => {
                                        const newWells = [...form.wells];
                                        newWells[index].well_depth = e.target.value ? Number(e.target.value) : null;
                                        const calculatedTDH = calculateTDH({ ...newWells[index], well_depth: e.target.value ? Number(e.target.value) : null });
                                        newWells[index].tdh = calculatedTDH;
                                        setForm({ ...form, wells: newWells });
                                      }}
                                      min={0}
                                      step={0.1}
                                      placeholder="0"
                                    />
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <label className="block text-xs font-medium text-gray-600">Height to Tank/Field (m)</label>
                                    <input
                                      type="number"
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                      value={well.height_to_tank || ''}
                                      onChange={(e) => {
                                        const newWells = [...form.wells];
                                        newWells[index].height_to_tank = e.target.value ? Number(e.target.value) : null;
                                        const calculatedTDH = calculateTDH({ ...newWells[index], height_to_tank: e.target.value ? Number(e.target.value) : null });
                                        newWells[index].tdh = calculatedTDH;
                                        setForm({ ...form, wells: newWells });
                                      }}
                                      min={0}
                                      step={0.1}
                                      placeholder="0"
                                    />
                                  </div>
                                  
                                  {/* End Use Pressure Section */}
                                  <div className="space-y-3">
                                    <label className="block text-sm font-medium text-gray-700">Where is the water going?</label>
                                    <div className="space-y-2">
                                      {[
                                        { value: 'open_tank', label: 'Open tank', pressure: '0.0 bar' },
                                        { value: 'drip_lines', label: 'Drip lines', pressure: '2.0 bar' },
                                        { value: 'sprinklers', label: 'Sprinklers', pressure: '2.5 bar' },
                                        { value: 'high_pressure_sprinkler', label: 'High-pressure sprinkler', pressure: '4.5 bar' },
                                        { value: 'pivot_linear', label: 'Pivot/linear', pressure: '1.0 bar' }
                                      ].map((option) => (
                                        <label key={option.value} className="flex items-center space-x-3 cursor-pointer group">
                                          <div className="relative">
                                            <input
                                              type="radio"
                                              name={`end_use_${well.id}`}
                                              className="sr-only"
                                              checked={well.end_use_type === option.value}
                                              onChange={() => {
                                                const newWells = [...form.wells];
                                                newWells[index].end_use_type = option.value as any;
                                                const computedPressure = calculateEndUsePressure(option.value, well.filter_allowance);
                                                newWells[index].computed_pressure_bar = computedPressure;
                                                const calculatedTDH = calculateTDH({ ...newWells[index], computed_pressure_bar: computedPressure });
                                                newWells[index].tdh = calculatedTDH;
                                                setForm({ ...form, wells: newWells });
                                              }}
                                            />
                                            <div className={`w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                              well.end_use_type === option.value 
                                                ? 'border-green-500 bg-green-500' 
                                                : 'border-gray-300 bg-white group-hover:border-green-400'
                                            }`}>
                                              {well.end_use_type === option.value && (
                                                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                              )}
                                            </div>
                                          </div>
                                          <span className="text-sm text-gray-700 group-hover:text-green-600 transition-colors">
                                            {option.label} — {option.pressure}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                    
                                    {/* Filter Allowance */}
                                    <div className="ml-7 space-y-2">
                                      <label className="block text-xs font-medium text-gray-600">Filters/regulators</label>
                                      <select
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                        value={well.filter_allowance}
                                        onChange={(e) => {
                                          const newWells = [...form.wells];
                                          newWells[index].filter_allowance = e.target.value as any;
                                          const computedPressure = calculateEndUsePressure(well.end_use_type, e.target.value);
                                          newWells[index].computed_pressure_bar = computedPressure;
                                          const calculatedTDH = calculateTDH({ ...newWells[index], computed_pressure_bar: computedPressure });
                                          newWells[index].tdh = calculatedTDH;
                                          setForm({ ...form, wells: newWells });
                                        }}
                                      >
                                        <option value="none">None — +0.0 bar</option>
                                        <option value="clean">Clean — +0.2 bar</option>
                                        <option value="dirty">Can get dirty — +0.4 bar</option>
                                      </select>
                                    </div>
                                    
                                    {/* Computed Pressure Display */}
                                    <div className="ml-7 p-2 bg-green-50 rounded-lg border border-green-200">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-green-800">Total pressure:</span>
                                        <span className="text-sm font-bold text-green-600">{well.computed_pressure_bar} bar</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Pipe Loss Section */}
                                  <div className="space-y-3">
                                    <label className="block text-sm font-medium text-gray-700">Pipes & bends (pick the closest)</label>
                                    <select
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                      value={well.pipe_configuration}
                                      onChange={(e) => {
                                        const newWells = [...form.wells];
                                        newWells[index].pipe_configuration = e.target.value as any;
                                        const computedPipeLoss = calculatePipeLoss(e.target.value, well.pipe_size);
                                        newWells[index].computed_pipe_loss_percent = computedPipeLoss * 100;
                                        const calculatedTDH = calculateTDH({ ...newWells[index], computed_pipe_loss_percent: computedPipeLoss * 100 });
                                        newWells[index].tdh = calculatedTDH;
                                        setForm({ ...form, wells: newWells });
                                      }}
                                    >
                                      <option value="short_0_50m_0_2b">Very short · 0–50 m · ≤2 bends → ~5–8 m</option>
                                      <option value="short_50_150m_≤4b">Short · 50–150 m · ≤4 bends → ~8–12 m</option>
                                      <option value="typical_150_300m_5_8b">Typical · 150–300 m · 5–8 bends → ~12–20 m</option>
                                      <option value="long_300_600m_9_12b">Long · 300–600 m · 9–12 bends → ~18–30 m</option>
                                      <option value="verylong_600_1000m_13_20b">Very long · 600–1000 m · 13–20 bends → ~25–40 m</option>
                                      <option value="extreme_>1000m_manyb">Extreme · &gt;1000 m · many bends/valves → ~35–55 m</option>
                                    </select>
                                    
                                    {/* Pipe Size */}
                                    <div className="space-y-2">
                                      <label className="block text-xs font-medium text-gray-600">Pipe size (inside width)</label>
                                      <select
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                                        value={well.pipe_size}
                                        onChange={(e) => {
                                          const newWells = [...form.wells];
                                          newWells[index].pipe_size = e.target.value as any;
                                          const computedPipeLoss = calculatePipeLoss(well.pipe_configuration, e.target.value);
                                          newWells[index].computed_pipe_loss_percent = computedPipeLoss * 100;
                                          const calculatedTDH = calculateTDH({ ...newWells[index], computed_pipe_loss_percent: computedPipeLoss * 100 });
                                          newWells[index].tdh = calculatedTDH;
                                          setForm({ ...form, wells: newWells });
                                        }}
                                      >
                                        <option value="small">Small (≤63 mm) — +20%</option>
                                        <option value="medium">Medium (75–90 mm) — no change</option>
                                        <option value="large">Large (≥110 mm) — -15%</option>
                                      </select>
                                    </div>
                                    
                                    {/* Computed Pipe Loss Display */}
                                    <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-blue-800">Pipe loss:</span>
                                        <span className="text-sm font-bold text-blue-600">{well.computed_pipe_loss_percent.toFixed(1)}%</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                  {/* Calculated TDH Display */}
                                  {well.tdh && (
                                    <div className="mt-3 p-2 bg-green-50 rounded-lg border border-green-200">
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-green-800">Calculated TDH:</span>
                                        <span className="text-lg font-bold text-green-600">{well.tdh} m</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Card Footer Info */}
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-xs text-green-700">
                            <span className="font-medium">Optional fields:</span> Measured flow, salinity, and TDH can be left empty if unknown.
                            <br />
                            <span className="font-medium">TDH Calculation:</span> Fill in well depth and height to tank/field to auto-calculate TDH.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Section 2: Pump Details */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Pump Details</h3>
                </div>
                
                <div className="space-y-6">
                  {/* Pump Selection Fields */}
                  <div className="flex flex-col lg:flex-row lg:items-start space-y-4 lg:space-y-0 lg:space-x-12">
                    <div className="space-y-2 flex-1">
                      <label className="block text-sm font-medium text-gray-700">Do you have pumps?</label>
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <div className="relative">
                            <input
                              type="radio"
                              name="has_pumps"
                              className="sr-only"
                              checked={form.has_pumps === true}
                              onChange={() => setForm({ ...form, has_pumps: true })}
                            />
                            <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                              form.has_pumps === true 
                                ? 'border-blue-500 bg-blue-500' 
                                : 'border-gray-300 bg-white group-hover:border-blue-400'
                            }`}>
                              {form.has_pumps === true && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Yes</span>
                        </label>
                        <label className="flex items-center space-x-3 cursor-pointer group">
                          <div className="relative">
                            <input
                              type="radio"
                              name="has_pumps"
                              className="sr-only"
                              checked={form.has_pumps === false}
                              onChange={() => setForm({ ...form, has_pumps: false })}
                            />
                            <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                              form.has_pumps === false 
                                ? 'border-blue-500 bg-blue-500' 
                                : 'border-gray-300 bg-white group-hover:border-blue-400'
                            }`}>
                              {form.has_pumps === false && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">No</span>
                        </label>
                      </div>
                    </div>
                    
                    {form.has_pumps && (
                      <div className="space-y-2 flex-1 max-w-xs">
                        <label className="block text-sm font-medium text-gray-700">Number of Pumps</label>
                        <input
                          type="number"
                          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={form.pumps_count}
                          onChange={(e) => {
                            const count = Math.max(0, Math.floor(Number(e.target.value) || 0));
                            const newPumps = Array.from({ length: count }, (_, i) => ({
                              id: i + 1,
                              pump_type: form.pumps[i]?.pump_type || 'submersible',
                              serves_well: form.pumps[i]?.serves_well || null,
                              pump_category: form.pumps[i]?.pump_category || 'electrical',
                              // Electrical pump fields
                              frequency: form.pumps[i]?.frequency || '50',
                              phase: form.pumps[i]?.phase || '3',
                              voltage: form.pumps[i]?.voltage || 400,
                              // Mechanical pump fields
                              horsepower: form.pumps[i]?.horsepower || 0,
                              consumption: form.pumps[i]?.consumption || 0,
                              consumption_timeframe: form.pumps[i]?.consumption_timeframe || 'daily',
                            }));
                            setForm({ ...form, pumps_count: count, pumps: newPumps });
                          }}
                          min={0}
                          placeholder="0"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Pump Details Cards */}
                  {form.has_pumps && form.pumps_count > 0 && (
                    <>
                      {/* Separator */}
                      <div className="border-t border-gray-200"></div>
                      
                      {/* Pump Details Cards */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <h5 className="text-sm font-semibold text-gray-800">Pump Specifications</h5>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                            {form.pumps_count} pump{form.pumps_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        <div className="space-y-4">
                          {form.pumps.map((pump, index) => (
                            <div key={pump.id} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-center space-x-4 mb-4">
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-sm font-semibold text-blue-600">{pump.id}</span>
                                  </div>
                                  <h6 className="text-sm font-semibold text-gray-800">Pump #{pump.id}</h6>
                                </div>
                              </div>
                              
                              {/* Pump Category Selection */}
                              <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-3">Pump Category</label>
                                <div className="flex space-x-4">
                                  <label className="flex items-center space-x-3 cursor-pointer group">
                                    <div className="relative">
                                      <input
                                        type="radio"
                                        name={`pump_category_${pump.id}`}
                                        className="sr-only"
                                        checked={pump.pump_category === 'electrical'}
                                        onChange={() => {
                                          const newPumps = [...form.pumps];
                                          newPumps[index].pump_category = 'electrical';
                                          setForm({ ...form, pumps: newPumps });
                                        }}
                                      />
                                      <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                        pump.pump_category === 'electrical' 
                                          ? 'border-blue-500 bg-blue-500' 
                                          : 'border-gray-300 bg-white group-hover:border-blue-400'
                                      }`}>
                                        {pump.pump_category === 'electrical' && (
                                          <div className="w-2 h-2 bg-white rounded-full"></div>
                                        )}
                                      </div>
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Electrical</span>
                                  </label>
                                  <label className="flex items-center space-x-3 cursor-pointer group">
                                    <div className="relative">
                                      <input
                                        type="radio"
                                        name={`pump_category_${pump.id}`}
                                        className="sr-only"
                                        checked={pump.pump_category === 'mechanical'}
                                        onChange={() => {
                                          const newPumps = [...form.pumps];
                                          newPumps[index].pump_category = 'mechanical';
                                          setForm({ ...form, pumps: newPumps });
                                        }}
                                      />
                                      <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                        pump.pump_category === 'mechanical' 
                                          ? 'border-blue-500 bg-blue-500' 
                                          : 'border-gray-300 bg-white group-hover:border-blue-400'
                                      }`}>
                                        {pump.pump_category === 'mechanical' && (
                                          <div className="w-2 h-2 bg-white rounded-full"></div>
                                        )}
                                      </div>
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Mechanical</span>
                                  </label>
                                </div>
                              </div>

                              {/* Common Fields */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div className="space-y-1">
                                  <label className="block text-xs font-medium text-gray-600">Pump Type</label>
                                  <select
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={pump.pump_type}
                                    onChange={(e) => {
                                      const newPumps = [...form.pumps];
                                      newPumps[index].pump_type = e.target.value as 'submersible' | 'surface';
                                      setForm({ ...form, pumps: newPumps });
                                    }}
                                  >
                                    <option value="submersible">Submersible</option>
                                    <option value="surface">Surface</option>
                                  </select>
                                </div>
                                
                                <div className="space-y-1">
                                  <label className="block text-xs font-medium text-gray-600">Serves Well</label>
                                  <select
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    value={pump.serves_well === null ? '' : String(pump.serves_well)}
                                    onChange={(e) => {
                                      const newPumps = [...form.pumps];
                                      if (e.target.value === 'others') {
                                        newPumps[index].serves_well = 'others';
                                      } else if (e.target.value) {
                                        newPumps[index].serves_well = Number(e.target.value);
                                      } else {
                                        newPumps[index].serves_well = null;
                                      }
                                      setForm({ ...form, pumps: newPumps });
                                    }}
                                  >
                                    <option value="">Select Well</option>
                                    {form.wells.map((well) => {
                                      // Check if this well is already selected by another pump
                                      const isWellSelected = form.pumps.some((otherPump, otherIndex) => 
                                        otherIndex !== index && otherPump.serves_well === well.id
                                      );
                                      
                                      return (
                                        <option 
                                          key={well.id} 
                                          value={well.id}
                                          disabled={isWellSelected}
                                        >
                                          Well #{well.id} {isWellSelected ? '(Already selected)' : ''}
                                        </option>
                                      );
                                    })}
                                    <option value="others">Others</option>
                                  </select>
                                </div>
                              </div>

                              {/* Conditional Fields Based on Pump Category */}
                              {pump.pump_category === 'electrical' ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div className="space-y-1">
                                    <label className="block text-xs font-medium text-gray-600">Frequency (Hz)</label>
                                    <select
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                      value={pump.frequency}
                                      onChange={(e) => {
                                        const newPumps = [...form.pumps];
                                        newPumps[index].frequency = e.target.value as '50' | '60';
                                        // Reset phase and voltage when frequency changes
                                        newPumps[index].phase = '3';
                                        newPumps[index].voltage = e.target.value === '50' ? 400 : 208;
                                        setForm({ ...form, pumps: newPumps });
                                      }}
                                    >
                                      <option value="50">50 Hz</option>
                                      <option value="60">60 Hz</option>
                                    </select>
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <label className="block text-xs font-medium text-gray-600">Phase</label>
                                    <select
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                      value={pump.phase}
                                      onChange={(e) => {
                                        const newPumps = [...form.pumps];
                                        newPumps[index].phase = e.target.value as '1' | '3';
                                        // Reset voltage when phase changes
                                        const voltageOptions = getVoltageOptions(pump.frequency || '50', e.target.value);
                                        newPumps[index].voltage = voltageOptions[0].value;
                                        setForm({ ...form, pumps: newPumps });
                                      }}
                                    >
                                      <option value="1">1-Phase</option>
                                      <option value="3">3-Phase</option>
                                    </select>
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <label className="block text-xs font-medium text-gray-600">Voltage</label>
                                    <select
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                      value={pump.voltage}
                                      onChange={(e) => {
                                        const newPumps = [...form.pumps];
                                        newPumps[index].voltage = Number(e.target.value);
                                        setForm({ ...form, pumps: newPumps });
                                      }}
                                    >
                                      {getVoltageOptions(pump.frequency || '50', pump.phase || '3').map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div className="space-y-1">
                                    <label className="block text-xs font-medium text-gray-600">Horsepower (HP)</label>
                                    <input
                                      type="number"
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                      value={pump.horsepower || ''}
                                      onChange={(e) => {
                                        const newPumps = [...form.pumps];
                                        newPumps[index].horsepower = Number(e.target.value) || 0;
                                        setForm({ ...form, pumps: newPumps });
                                      }}
                                      min={0}
                                      step={0.1}
                                      placeholder="0"
                                    />
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <label className="block text-xs font-medium text-gray-600">Consumption (Liters)</label>
                                    <input
                                      type="number"
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                      value={pump.consumption || ''}
                                      onChange={(e) => {
                                        const newPumps = [...form.pumps];
                                        newPumps[index].consumption = Number(e.target.value) || 0;
                                        setForm({ ...form, pumps: newPumps });
                                      }}
                                      min={0}
                                      step={0.1}
                                      placeholder="0"
                                    />
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <label className="block text-xs font-medium text-gray-600">Timeframe</label>
                                    <select
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                      value={pump.consumption_timeframe}
                                      onChange={(e) => {
                                        const newPumps = [...form.pumps];
                                        newPumps[index].consumption_timeframe = e.target.value as 'daily' | 'weekly' | 'monthly';
                                        setForm({ ...form, pumps: newPumps });
                                      }}
                                    >
                                      <option value="daily">Daily</option>
                                      <option value="weekly">Weekly</option>
                                      <option value="monthly">Monthly</option>
                                    </select>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Card Footer Info */}
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-blue-700">
                            <span className="font-medium">Pump Type:</span> Submersible pumps go inside the well, surface pumps stay above ground.
                            <br />
                            <span className="font-medium">Serves Well:</span> Select which well this pump is connected to.
                            <br />
                            <span className="font-medium">Voltage:</span> Choose the voltage that matches your electrical supply.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>


        {/* Section 3: Water Demand & Schedule */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
          <div className="flex items-center mb-6">
            <div className="p-2 bg-green-100 rounded-lg mr-3">
              <Droplets className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Water Demand & Schedule</h3>
          </div>
                
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Daily Water Need (m³/day) *</label>
                    <input
                      type="number"
                      className={`block w-full rounded-xl border-2 ${errors.daily_water_need ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'} px-4 py-3 text-gray-700 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all duration-200`}
                      value={form.daily_water_need}
                      onChange={(e) => setForm({ ...form, daily_water_need: Number(e.target.value) || 0 })}
                      min={0}
                      placeholder="Enter daily water need"
                    />
                    {errors.daily_water_need && (
                      <p className="text-sm text-red-600 flex items-center"><AlertCircle className="h-4 w-4 mr-1" />{errors.daily_water_need}</p>
                    )}
                    {waterEstimateApplied && (
                      <p className="text-sm text-green-600 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Filled from helper estimate.
                      </p>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-semibold text-gray-700">Don't know daily water need?</label>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, use_helper: !form.use_helper })}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {form.use_helper ? 'Hide Helper' : 'Use Helper'}
                      </button>
                    </div>
                    
                    {form.use_helper && (
                      <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Area (ha)</label>
                          <input
                            type="number"
                            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            value={form.helper_area}
                            onChange={(e) => setForm({ ...form, helper_area: Number(e.target.value) || 0 })}
                            min={0}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Crop</label>
                          <select
                            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            value={form.helper_crop}
                            onChange={(e) => setForm({ ...form, helper_crop: e.target.value })}
                          >
                            <option value="">Select Crop</option>
                            <option value="date palms">Date Palms</option>
                            <option value="alfalfa">Alfalfa</option>
                            <option value="wheat">Wheat</option>
                            <option value="vegetables">Vegetables</option>
                            <option value="orchards">Orchards</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Method</label>
                          <select
                            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            value={form.helper_method}
                            onChange={(e) => setForm({ ...form, helper_method: e.target.value })}
                          >
                            <option value="">Select Method</option>
                            <option value="drip">Drip</option>
                            <option value="sprinkler">Sprinkler</option>
                            <option value="flood">Flood</option>
                            <option value="mixed">Mixed</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Hours/day</label>
                          <input
                            type="number"
                            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            value={form.helper_hours}
                            onChange={(e) => setForm({ ...form, helper_hours: Number(e.target.value) || 6 })}
                            min={1}
                            max={24}
                            placeholder="6"
                          />
                        </div>
                      </div>
                      
                      {/* Live Estimate Display */}
                      {(() => {
                        const estimate = calculateWaterEstimate();
                        return estimate ? (
                          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="text-center">
                              <div className="text-lg font-bold text-green-700 mb-1">
                                Estimated daily water: {estimate.dailyM3} m³/day
                              </div>
                              <div className="text-sm text-green-600 mb-3">
                                Target flow: {estimate.targetFlow} m³/h (Daily ÷ Hours)
                              </div>
                              {estimate.dailyM3 < 5 || estimate.dailyM3 > 10000 ? (
                                <p className="text-sm text-orange-600 mb-3">
                                  This looks unusual—please recheck area/method.
                                </p>
                              ) : null}
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setForm({
                                      ...form,
                                      daily_water_need: estimate.dailyM3,
                                      use_helper: false
                                    });
                                    setWaterEstimateApplied(true);
                                  }}
                                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                                >
                                  Use estimate
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setForm({ ...form, use_helper: false })}
                                  className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600 text-center">
                              Complete the fields to see an estimate
                            </p>
                          </div>
                        );
                      })()}
                      </>
                    )}
                  </div>
                </div>
              </div>


              {/* Section 4: Power + Optional Uploads */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-orange-100 rounded-lg mr-3">
                    <Zap className="h-5 w-5 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Power Sources</h3>
                </div>
                
                <div className="space-y-8">
                  {/* Power Source Selection */}
                  <div className="space-y-4">
                    <label className="block text-sm font-semibold text-gray-700">Available Power Sources</label>
                    <p className="text-sm text-gray-600">Select all power sources you have available:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-center p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer hover:bg-gray-50 group">
                        <div className="relative">
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={form.use_grid}
                            onChange={(e) => setForm({ ...form, use_grid: e.target.checked })}
                          />
                          <div className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
                            form.use_grid 
                              ? 'border-orange-500 bg-orange-500' 
                              : 'border-gray-300 bg-white group-hover:border-orange-400'
                          }`}>
                            {form.use_grid && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3 ml-3">
                          <div className="text-2xl">⚡</div>
                          <div>
                            <div className="font-medium text-sm group-hover:text-orange-600 transition-colors">Use Grid</div>
                            <div className="text-xs text-gray-500">I have electric grid connection</div>
                          </div>
                        </div>
                      </label>
                      
                      <label className="flex items-center p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer hover:bg-gray-50 group">
                        <div className="relative">
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={form.use_diesel}
                            onChange={(e) => setForm({ ...form, use_diesel: e.target.checked })}
                          />
                          <div className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
                            form.use_diesel 
                              ? 'border-orange-500 bg-orange-500' 
                              : 'border-gray-300 bg-white group-hover:border-orange-400'
                          }`}>
                            {form.use_diesel && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3 ml-3">
                          <div className="text-2xl">⛽</div>
                          <div>
                            <div className="font-medium text-sm group-hover:text-orange-600 transition-colors">Use Diesel Generator</div>
                            <div className="text-xs text-gray-500">I have a diesel generator</div>
                          </div>
                        </div>
                      </label>
                    </div>
                    
                    {!form.use_grid && !form.use_diesel && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-sm text-yellow-700">
                          Please select at least one power source.
                        </p>
                      </div>
                    )}
                    {errors.power_sources && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.power_sources}
                      </p>
                    )}
                  </div>

                  {/* Grid Configuration */}
                  {form.use_grid && (
                    <div className="space-y-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <h5 className="text-sm font-semibold text-gray-800">Grid Connection</h5>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="space-y-3">
                          <label className="block text-sm font-medium text-gray-700">Is your grid the same as your pump supply?</label>
                          <div className="flex space-x-4">
                            <label className="flex items-center space-x-3 cursor-pointer group">
                              <div className="relative">
                                <input
                                  type="radio"
                                  name="grid_same_as_pump"
                                  className="sr-only"
                                  checked={form.grid_same_as_pump}
                                  onChange={() => {
                                    setForm({ ...form, grid_same_as_pump: true });
                                  // Auto-fill from largest electrical pump
                                  if (form.pumps.length > 0) {
                                    const largestPump = getLargestPump();
                                    setForm({
                                      ...form,
                                      grid_same_as_pump: true,
                                      grid_phase: largestPump.phase,
                                      grid_voltage: largestPump.voltage
                                    });
                                  }
                                  }}
                                />
                                <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                  form.grid_same_as_pump 
                                    ? 'border-orange-500 bg-orange-500' 
                                    : 'border-gray-300 bg-white group-hover:border-orange-400'
                                }`}>
                                  {form.grid_same_as_pump && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm font-medium text-gray-700 group-hover:text-orange-600 transition-colors">Yes (default)</span>
                            </label>
                            <label className="flex items-center space-x-3 cursor-pointer group">
                              <div className="relative">
                                <input
                                  type="radio"
                                  name="grid_same_as_pump"
                                  className="sr-only"
                                  checked={!form.grid_same_as_pump}
                                  onChange={() => setForm({ ...form, grid_same_as_pump: false })}
                                />
                                <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                  !form.grid_same_as_pump 
                                    ? 'border-orange-500 bg-orange-500' 
                                    : 'border-gray-300 bg-white group-hover:border-orange-400'
                                }`}>
                                  {!form.grid_same_as_pump && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm font-medium text-gray-700 group-hover:text-orange-600 transition-colors">No</span>
                            </label>
                          </div>
                        </div>

                        {!form.grid_same_as_pump && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Grid Phase</label>
                              <select
                                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                value={form.grid_phase}
                                onChange={(e) => setForm({ ...form, grid_phase: e.target.value as '1' | '3' })}
                              >
                                <option value="1">1-Phase</option>
                                <option value="3">3-Phase</option>
                              </select>
                            </div>
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Grid Voltage</label>
                              <select
                                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                value={form.grid_voltage}
                                onChange={(e) => setForm({ ...form, grid_voltage: Number(e.target.value) })}
                              >
                                <option value={230}>230V</option>
                                <option value={400}>400V</option>
                                <option value={415}>415V</option>
                                <option value={208}>208V</option>
                                <option value={480}>480V</option>
                                <option value={600}>600V</option>
                                <option value={690}>690V</option>
                              </select>
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Export to Grid</label>
                          <div className="flex space-x-4">
                            <label className="flex items-center space-x-3 cursor-pointer group">
                              <div className="relative">
                                <input
                                  type="radio"
                                  name="grid_export"
                                  className="sr-only"
                                  checked={!form.grid_export_allowed}
                                  onChange={() => setForm({ ...form, grid_export_allowed: false })}
                                />
                                <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                  !form.grid_export_allowed 
                                    ? 'border-orange-500 bg-orange-500' 
                                    : 'border-gray-300 bg-white group-hover:border-orange-400'
                                }`}>
                                  {!form.grid_export_allowed && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm font-medium text-gray-700 group-hover:text-orange-600 transition-colors">No export (default)</span>
                            </label>
                            <label className="flex items-center space-x-3 cursor-pointer group">
                              <div className="relative">
                                <input
                                  type="radio"
                                  name="grid_export"
                                  className="sr-only"
                                  checked={form.grid_export_allowed}
                                  onChange={() => setForm({ ...form, grid_export_allowed: true })}
                                />
                                <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                  form.grid_export_allowed 
                                    ? 'border-orange-500 bg-orange-500' 
                                    : 'border-gray-300 bg-white group-hover:border-orange-400'
                                }`}>
                                  {form.grid_export_allowed && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm font-medium text-gray-700 group-hover:text-orange-600 transition-colors">Export allowed</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Diesel Configuration */}
                  {form.use_diesel && (
                    <div className="space-y-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <h5 className="text-sm font-semibold text-gray-800">Diesel Generator</h5>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="space-y-3">
                          <label className="block text-sm font-medium text-gray-700">Is the generator the same as your pump/grid?</label>
                          <div className="flex space-x-4">
                            <label className="flex items-center space-x-3 cursor-pointer group">
                              <div className="relative">
                                <input
                                  type="radio"
                                  name="diesel_same_as_grid_or_pump"
                                  className="sr-only"
                                  checked={form.diesel_same_as_grid_or_pump}
                                  onChange={() => {
                                    setForm({ ...form, diesel_same_as_grid_or_pump: true });
                                    // Auto-fill from grid or largest pump
                                    if (form.use_grid) {
                                      setForm({
                                        ...form,
                                        diesel_same_as_grid_or_pump: true,
                                        diesel_phase: form.grid_phase,
                                        diesel_voltage: form.grid_voltage
                                      });
                                    } else if (form.pumps.length > 0) {
                                      const largestPump = getLargestPump();
                                      setForm({
                                        ...form,
                                        diesel_same_as_grid_or_pump: true,
                                        diesel_phase: largestPump.phase,
                                        diesel_voltage: largestPump.voltage
                                      });
                                    }
                                  }}
                                />
                                <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                  form.diesel_same_as_grid_or_pump 
                                    ? 'border-orange-500 bg-orange-500' 
                                    : 'border-gray-300 bg-white group-hover:border-orange-400'
                                }`}>
                                  {form.diesel_same_as_grid_or_pump && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm font-medium text-gray-700 group-hover:text-orange-600 transition-colors">Yes (default)</span>
                            </label>
                            <label className="flex items-center space-x-3 cursor-pointer group">
                              <div className="relative">
                                <input
                                  type="radio"
                                  name="diesel_same_as_grid_or_pump"
                                  className="sr-only"
                                  checked={!form.diesel_same_as_grid_or_pump}
                                  onChange={() => setForm({ ...form, diesel_same_as_grid_or_pump: false })}
                                />
                                <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                                  !form.diesel_same_as_grid_or_pump 
                                    ? 'border-orange-500 bg-orange-500' 
                                    : 'border-gray-300 bg-white group-hover:border-orange-400'
                                }`}>
                                  {!form.diesel_same_as_grid_or_pump && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm font-medium text-gray-700 group-hover:text-orange-600 transition-colors">No</span>
                            </label>
                          </div>
                        </div>

                        {!form.diesel_same_as_grid_or_pump && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Generator Size (kW) *</label>
                              <input
                                type="number"
                                className={`block w-full rounded-lg border ${errors.diesel_size_kw ? 'border-red-300 bg-red-50' : 'border-gray-300'} px-3 py-2 text-sm`}
                                value={form.diesel_size_kw}
                                onChange={(e) => setForm({ ...form, diesel_size_kw: Number(e.target.value) || 0 })}
                                min={0}
                                step={0.1}
                                placeholder="0"
                              />
                              {errors.diesel_size_kw && (
                                <p className="text-xs text-red-600">{errors.diesel_size_kw}</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Phase</label>
                              <select
                                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                value={form.diesel_phase}
                                onChange={(e) => setForm({ ...form, diesel_phase: e.target.value as '1' | '3' })}
                              >
                                <option value="1">1-Phase</option>
                                <option value="3">3-Phase</option>
                              </select>
                            </div>
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Voltage</label>
                              <select
                                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                value={form.diesel_voltage}
                                onChange={(e) => setForm({ ...form, diesel_voltage: Number(e.target.value) })}
                              >
                                <option value={230}>230V</option>
                                <option value={400}>400V</option>
                                <option value={415}>415V</option>
                                <option value={208}>208V</option>
                                <option value={480}>480V</option>
                                <option value={600}>600V</option>
                                <option value={690}>690V</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}


                  {/* Optional Uploads */}
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Optional Document Uploads (for better results)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Pump Datasheet</label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          onChange={(e) => setForm({ ...form, pump_datasheet: e.target.files?.[0] || null })}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Generator Datasheet</label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          onChange={(e) => setForm({ ...form, generator_datasheet: e.target.files?.[0] || null })}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Energy Bill (deisel, electrical, etc.)</label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          onChange={(e) => setForm({ ...form, electricity_bill: e.target.files?.[0] || null })}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Site Photos</label>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          multiple
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          onChange={(e) => setForm({ ...form, site_photos: e.target.files?.[0] || null })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Consent Checkboxes */}
                  <div className="border-t pt-4">
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id="contact-consent"
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={form.contact_consent}
                          onChange={(e) => setForm({ ...form, contact_consent: e.target.checked })}
                        />
                        <label htmlFor="contact-consent" className="text-sm text-gray-700">
                          I consent to being contacted about this project *
                        </label>
                      </div>
                      {errors.contact_consent && (
                        <p className="text-sm text-red-600">{errors.contact_consent}</p>
                      )}
                      
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id="data-use-consent"
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={form.data_use_consent}
                          onChange={(e) => setForm({ ...form, data_use_consent: e.target.checked })}
                        />
                        <label htmlFor="data-use-consent" className="text-sm text-gray-700">
                          I consent to the use of my data for project analysis *
                        </label>
                      </div>
                      {errors.data_use_consent && (
                        <p className="text-sm text-red-600">{errors.data_use_consent}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                    <div>
                      <h4 className="text-sm font-medium text-red-800">Error Saving Data</h4>
                      <p className="text-sm text-red-600 mt-1">{saveError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={onBack}
                  disabled={isLoading}
                  className="px-6 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl font-medium hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving Agricultural Data...
                    </div>
                  ) : (
                    'Continue to Location'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

    </div>
  );
}